import React, {useState} from 'react';
import {Textarea} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {DateInput} from '@mantine/dates';
import {IconTrash} from '@tabler/icons-react';
import {ActionIcon} from 'src/design/atoms/ActionIcon';
import {Button} from 'src/design/atoms/Button';
import {Spinner} from 'src/design/atoms/Spinner';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {PersonSelectBox} from 'src/app/databound-elements/PersonSelectBox';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {formatUtcShortDate} from 'src/shared/utils/formatters';

export interface AssignmentRuleExceptionEntry {
  id: number;
  userId: number;
  userName?: string;
  reason: string;
  expiresAt?: Date;
}

export interface AssignmentRuleExceptionCreateRequest {
  ruleId: number;
  userId: number;
  reason: string;
  expiresAt?: Date;
}

export interface AssignmentRuleExceptionsProps {
  ruleId: number;
  canEdit: boolean;
  /** Copy verb for the rule's positive membership state, e.g. "assigned" / "enrolled". */
  matchedVerb: string;
  useListExceptionsQuery: (ruleId: number) => {
    data?: AssignmentRuleExceptionEntry[];
    isLoading: boolean;
  };
  useAddExceptionMutation: () => readonly [
    (request: AssignmentRuleExceptionCreateRequest) => {
      unwrap: () => Promise<unknown>;
    },
    {isLoading: boolean}
  ];
  useRemoveExceptionMutation: () => readonly [
    (id: number) => {unwrap: () => Promise<unknown>},
    unknown
  ];
}

/** Exemption list for a rule: person + reason + optional expiry, excluded from the rule's resolved count. */
export const AssignmentRuleExceptions: React.FC<
  AssignmentRuleExceptionsProps
> = ({
  ruleId,
  canEdit,
  matchedVerb,
  useListExceptionsQuery,
  useAddExceptionMutation,
  useRemoveExceptionMutation,
}) => {
  const {setAlert} = useAlert();
  const {data, isLoading} = useListExceptionsQuery(ruleId);
  const [addException, {isLoading: isAdding}] = useAddExceptionMutation();
  const [removeException] = useRemoveExceptionMutation();

  const [addingUserId, setAddingUserId] = useState<string | undefined>();
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [pendingRemoveId, setPendingRemoveId] = useState<number | undefined>();

  const exceptions = data ?? [];

  const handleAdd = async () => {
    if (!addingUserId || !reason.trim()) return;
    try {
      await addException({
        ruleId,
        userId: Number(addingUserId),
        reason: reason.trim(),
        expiresAt: expiresAt ?? undefined,
      }).unwrap();
      setAddingUserId(undefined);
      setReason('');
      setExpiresAt(null);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleRemove = async () => {
    if (pendingRemoveId === undefined) return;
    try {
      await removeException(pendingRemoveId).unwrap();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setPendingRemoveId(undefined);
    }
  };

  return (
    <Box data-testid={`rule-exceptions-${ruleId}`}>
      {isLoading ? (
        <Spinner loadingText="Loading exceptions..." />
      ) : exceptions.length === 0 ? (
        <Text size="sm" c="dimmed" mb="sm">
          No exceptions. Everyone this rule matches is {matchedVerb}.
        </Text>
      ) : (
        <Stack gap="xs" mb="sm">
          {exceptions.map(exception => (
            <Group key={exception.id} justify="space-between" wrap="nowrap">
              <Box>
                <Text size="sm">
                  {exception.userName ?? `#${exception.userId}`}
                </Text>
                <Text size="xs" c="dimmed">
                  {exception.reason}
                  {exception.expiresAt &&
                    `. Expires ${formatUtcShortDate(exception.expiresAt)}`}
                </Text>
              </Box>
              {canEdit && (
                <ActionIcon
                  tooltipText={`Remove exception for ${exception.userName ?? exception.userId}`}
                  color="red"
                  onClick={() => setPendingRemoveId(exception.id)}>
                  <IconTrash size={16} />
                </ActionIcon>
              )}
            </Group>
          ))}
        </Stack>
      )}

      {canEdit && (
        <Stack gap="xs">
          <Group align="flex-end" wrap="wrap">
            <Box style={{minWidth: '220px', flex: 1}}>
              <PersonSelectBox
                label="Add exception for"
                value={addingUserId}
                onSelectChangeCallback={option =>
                  setAddingUserId(option?.id ? String(option.id) : undefined)
                }
              />
            </Box>
            <Box style={{width: '180px'}}>
              <DateInput
                label="Expires (optional)"
                placeholder="Never"
                value={expiresAt}
                onChange={setExpiresAt}
                clearable
              />
            </Box>
          </Group>
          <Textarea
            label="Reason"
            required
            placeholder="Why this person is exempt"
            value={reason}
            onChange={e => setReason(e.currentTarget.value)}
          />
          {!addingUserId || !reason.trim() ? (
            <Alert color="gray" py="xs">
              Select a person and enter a reason to add an exception.
            </Alert>
          ) : null}
          <Button
            size="sm"
            variant="subtle"
            disabled={!addingUserId || !reason.trim() || isAdding}
            loading={isAdding}
            onClick={handleAdd}>
            Add exception
          </Button>
        </Stack>
      )}

      <ConfirmDialog
        opened={pendingRemoveId !== undefined}
        onClose={() => setPendingRemoveId(undefined)}
        onConfirm={handleRemove}
        title="Remove exception?"
        body={`This person will be re-${matchedVerb} the next time the rule recalculates, unless they no longer match the rule's audience.`}
      />
    </Box>
  );
};
